import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, addToast } from '@heroui/react';
import { Award, Plus, Search } from 'lucide-react';
import CertificatesTable from './components/CertificatesTable.js';
import CredentialSection from './components/CredentialSection.js';
import CertificateDialog from './components/CertificateDialog.js';
import { readTypes, readGrades, readRatings, readCertificates } from './certificateCatalogs.js';
import { addCertificate, saveCertificate, deleteCertificate } from './certificatesApi.js';
import { toApiDate } from './documentDates.js';
import CredentialsApi, { apiErrorText } from './credentialsApi.js';
import { UPLOAD_POLICIES, uploadKindOf, uploadEach, uploadSummary } from './attachments.js';
import useSectionRefresh from './useSectionRefresh.js';

/**
 * CertificatesPage — `/users/certificates/`.
 *
 * Renders from the Symfony payload (certificates + catalogs) and writes through
 * `certificatesApi` on Symfony (a medical certificate is created without a
 * number).
 *
 * As on the Documents tab, a successful write reloads rather than patching
 * local state: the server derives the medical expiries and reminders, so
 * re-reading is the only honest confirmation.
 */
function CertificatesPage({ payload, onRefresh }) {
	const certificates = useMemo(() => readCertificates(payload), [payload]);
	const types = useMemo(() => readTypes(payload), [payload]);
	const grades = useMemo(() => readGrades(payload), [payload]);
	const ratings = useMemo(() => readRatings(payload), [payload]);

	const [query, setQuery] = useState('');
	const [editing, setEditing] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	// The side panel opens on the record (view) and the pencil turns it into the form.
	const [mode, setMode] = useState('view');
	const [saving, setSaving] = useState(false);
	const [busyId, setBusyId] = useState(null);
	const [uploading, setUploading] = useState(false);
	const refresh = useSectionRefresh(onRefresh);

	// After a re-read the open drawer shows the record as now stored.
	useEffect(() => {
		setEditing(prev =>
			prev ? certificates.find(c => String(c.id) === String(prev.id)) || prev : prev
		);
	}, [certificates]);

	// Scans are their own resource on Symfony; they touch no dates.
	const handleUploadAsset = useCallback(
		async (certificateId, files) => {
			setUploading(true);
			const result = await uploadEach(
				files,
				file => CredentialsApi.uploadCertificateAsset(certificateId, file, uploadKindOf(file)),
				{ minImageSide: UPLOAD_POLICIES.documents.minImageSide }
			);
			if (result.done) await refresh();
			setUploading(false);
			addToast(uploadSummary(result, apiErrorText));
		},
		[refresh]
	);

	const handleDeleteAsset = useCallback(async (certificateId, item) => {
		// eslint-disable-next-line no-alert
		if (!window.confirm(`Remove ${item.name}?`)) return;
		setUploading(true);
		try {
			await CredentialsApi.removeCertificateAsset(certificateId, item.kind, item.id);
			await refresh();
			setUploading(false);
			addToast({ title: 'Scan removed', color: 'success' });
		} catch (e) {
			setUploading(false);
			addToast({ title: 'Could not remove the scan', description: apiErrorText(e), color: 'danger' });
		}
	}, [refresh]);

	const typeName = useCallback(id => (types.find(t => t.id === String(id)) || {}).name, [types]);
	const gradeName = useCallback(id => (grades.find(g => g.id === String(id)) || {}).name, [grades]);

	const visible = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return certificates;
		return certificates.filter(c =>
			[typeName(c.type), gradeName(c.grade), c.number]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
				.includes(q)
		);
	}, [certificates, query, typeName, gradeName]);

	const openAdd = useCallback(() => {
		setEditing(null);
		setMode('edit');
		setDialogOpen(true);
	}, []);

	const openView = useCallback(cert => {
		setEditing(cert);
		setMode('view');
		setDialogOpen(true);
	}, []);

	const openEdit = useCallback(cert => {
		setEditing(cert);
		setMode('edit');
		setDialogOpen(true);
	}, []);

	const handleSave = useCallback(
		async (form, fields) => {
			setSaving(true);
			try {
				const medical = Boolean(fields.dateExpiresMedical);
				// A medical expiry is the server's to compute — the client's date
				// counts only for a special issuance.
				const special = medical && Boolean(form.specialIssuance);
				const expires = medical
					? special
						? toApiDate(form.dateExpires)
						: null
					: fields.dateExpires
						? toApiDate(form.dateExpires)
						: null;

				// A field the chosen type has goes with its value (even '' — that
				// clears it); one it lacks goes as null, which the update reads as
				// "leave it". The create DTO has non-nullable defaults, so nulls are
				// dropped there.
				const body = {
					typeId: Number(form.type),
					gradeId: fields.grade && form.grade ? Number(form.grade) : null,
					number: fields.certificateNumber ? form.number.trim() : null,
					dateOfIssue: fields.dateOfIssue ? toApiDate(form.dateOfIssue) : null,
					dateExpires: expires,
					frn: fields.frn ? form.frn : null,
					fileNumber: fields.fileNumber && form.fileNumber !== '' ? Number(form.fileNumber) : null,
					// The API refuses blank limitations; legacy's empty value is NONE.
					limitations: fields.limitations ? String(form.limitations || '').trim() || 'NONE' : null,
					specialIssuance: medical ? special : null
				};

				// Ratings are their own resource: diff the chosen set against what
				// is attached (rating ids in, relation ids out).
				const attached = form.id && editing && Array.isArray(editing.ratings) ? editing.ratings : [];
				const chosen = (form.ratings || []).map(String);
				const remove = attached.filter(r => !chosen.includes(String(r.id))).map(r => r.relId);
				const add = chosen.filter(id => !attached.some(r => String(r.id) === id));

				if (form.id) {
					await saveCertificate(form.id, body, { add, remove });
				} else {
					// A medical certificate has no number (the API accepts that since G10).
					const createBody = Object.fromEntries(
						Object.entries({ ...body, specialIssuance: special }).filter(([, v]) => v !== null)
					);
					await addCertificate(createBody, add);
				}

				await refresh();
				setSaving(false);
				addToast({ title: form.id ? 'Certificate saved' : 'Certificate added', color: 'success' });
				// An edit returns to the record's preview; a new one closes the panel.
				if (form.id) setMode('view');
				else setDialogOpen(false);
			} catch (e) {
				setSaving(false);
				addToast({
					title: form.id ? 'Could not save the certificate' : 'Could not add the certificate',
					description: apiErrorText(e),
					color: 'danger'
				});
			}
		},
		[editing, refresh]
	);

	const handleDelete = useCallback(
		async cert => {
			const label = typeName(cert.type) || 'this certificate';
			// eslint-disable-next-line no-alert
			if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
			setBusyId(cert.id);
			try {
				await deleteCertificate(cert.id);
				await refresh();
				setBusyId(null);
				setDialogOpen(false);
				addToast({ title: 'Certificate deleted', color: 'success' });
			} catch (e) {
				setBusyId(null);
				addToast({
					title: 'Could not delete the certificate',
					description: apiErrorText(e),
					color: 'danger'
				});
			}
		},
		[typeName, refresh]
	);

	return (
		<CredentialSection
			id="certificates"
			icon={Award}
			title="Certificates"
			description="Pilot, medical and instructor certificates, with their ratings."
			count={certificates.length}
			action={
				<Button
					color="primary"
					radius="sm"
					startContent={<Plus className="w-4 h-4" />}
					onPress={openAdd}
				>
					Add Certificate
				</Button>
			}
		>
			<Input
				aria-label="Search certificates"
				placeholder="Search certificates"
				variant="bordered"
				radius="sm"
				value={query}
				onValueChange={setQuery}
				startContent={<Search className="w-4 h-4 text-default-400" />}
				classNames={{ inputWrapper: 'h-10 min-h-10 bg-content1' }}
			/>

			<CertificatesTable
				certificates={visible}
				typeName={typeName}
				gradeName={gradeName}
				onView={openView}
				onEdit={openEdit}
				onDelete={handleDelete}
				busyId={busyId}
			/>

			<CertificateDialog
				isOpen={dialogOpen}
				onClose={() => setDialogOpen(false)}
				mode={mode}
				onModeChange={setMode}
				onDelete={handleDelete}
				deleting={editing != null && busyId === editing.id}
				certificate={editing}
				types={types}
				grades={grades}
				ratings={ratings}
				onSave={handleSave}
				saving={saving}
				onUploadAsset={handleUploadAsset}
				onDeleteAsset={handleDeleteAsset}
				uploading={uploading}
			/>
		</CredentialSection>
	);
}

export default CertificatesPage;
